/**
 * All canonical statuses used in code.
 */
export type AdvisorApplicationStatus =
  | "in_review"
  | "qualified_app"
  | "waitlist"
  | "interview"
  | "no_show"
  | "meeting"
  | "ready_ttt"
  | "ttt_sent"
  | "send_offer"
  | "offer_sent"
  | "accepted"
  | "onboarding"
  | "active"
  | "on_hold"
  | "rejected"
  | "churned"
  | "never_active";

// Status order from advisorFlow.ts
export const APPLICATION_STATUS_ORDER: AdvisorApplicationStatus[] = [
  "in_review",
  "qualified_app",
  "waitlist",
  "interview",
  "no_show",
  "meeting",
  "ready_ttt",
  "ttt_sent",
  "send_offer",
  "offer_sent",
  "accepted",
  "onboarding",
  "active",
  "on_hold",
  "rejected",
  "churned",
  "never_active",
];

/**
 * Optional tag to help your UI style different actions.
 *  • primary  → happy-path/forward progress
 *  • warning  → temporary/on-hold
 *  • error    → negative or terminal path
 */
interface NextStatusOption {
  name: AdvisorApplicationStatus;
  variant?: "primary" | "warning" | "error";
}

/**
 * Mapping: current status → valid next-status options.
 */
export const NEXT_PIPELINE_STATUS_MAP: Record<
  AdvisorApplicationStatus,
  NextStatusOption[]
> = {
  in_review: [
    { name: "qualified_app", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  qualified_app: [
    { name: "interview", variant: "primary" },
    { name: "rejected", variant: "error" },
    { name: "waitlist", variant: "warning" },
  ],

  interview: [
    { name: "meeting", variant: "primary" },
    { name: "no_show", variant: "warning" },
    { name: "rejected", variant: "error" },
  ],

  no_show: [
    { name: "interview", variant: "primary" }, // reschedule
    { name: "rejected", variant: "error" },
  ],

  meeting: [
    { name: "ready_ttt", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  ready_ttt: [
    { name: "ttt_sent", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  ttt_sent: [
    { name: "send_offer", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  send_offer: [
    { name: "offer_sent", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  offer_sent: [
    { name: "accepted", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  accepted: [
    { name: "onboarding", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  onboarding: [
    { name: "active", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],

  active: [
    { name: "on_hold", variant: "warning" },
    { name: "churned", variant: "error" },
  ],

  on_hold: [
    { name: "active", variant: "primary" },
    { name: "churned", variant: "error" },
    { name: "rejected", variant: "error" },
  ],

  // Terminal states (no forward transitions)
  rejected: [],
  churned: [],
  never_active: [],

  // Holding / backlog list
  waitlist: [
    { name: "in_review", variant: "primary" },
    { name: "rejected", variant: "error" },
  ],
};
